import type React from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type ModalType = 'quickAdd' | 'itemForm' | 'confirm' | null;
type ViewMode = 'card' | 'weekly' | 'monthly';
export type PageKey = 'tasks' | 'expenses' | 'notes' | 'dates' | 'search' | 'choreWheel' | 'dashboard' | 'notifications';
export type GroupBy = 'date' | 'category' | 'status' | 'priority' | 'owner' | 'itemGroup';
export type TabKey = 'all' | 'allScheduled' | 'allUnscheduled' | 'allRecurring' | 'pastWeek' | 'today' | 'nextWeek';
// Search page uses different tabs
export type SearchTabKey = 'scheduled' | 'recurring';

interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  duration?: number;
}

interface ConfirmConfig {
  title: string;
  message: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  saveText?: string;
  variant?: 'danger' | 'warning' | 'default' | 'info';
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
  onSave?: () => void | Promise<void>;
}

interface CategoryFilter {
  categoryId: string | null;
  subCategoryId: string | null;
}

interface PageFilters {
  ownerFilter: string | null;
  itemGroupFilter: string | null;
  statusFilter: string | null;
  priorityFilter: string | null;
}

interface UIState {
  // Sidebar
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;

  // Reference Date for Today tab (affects Past Week/Next Week calculations)
  referenceDate: Record<PageKey, number>;  // Store as timestamp for persistence
  setReferenceDate: (page: PageKey, date: Date) => void;
  resetReferenceDate: (page: PageKey) => void;
  getReferenceDate: (page: PageKey) => Date;

  // Modals
  activeModal: ModalType;
  modalData: Record<string, unknown> | null;
  openModal: (type: ModalType, data?: Record<string, unknown>) => void;
  closeModal: () => void;

  // Confirm dialog
  confirmConfig: ConfirmConfig | null;
  showConfirm: (config: ConfirmConfig) => void;
  hideConfirm: () => void;

  // Toasts
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;

  // Loading
  globalLoading: boolean;
  setGlobalLoading: (loading: boolean) => void;

  // Search
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searchTab: SearchTabKey;
  setSearchTab: (tab: SearchTabKey) => void;

  // Tab Filters (per page) - persisted
  activeTab: Record<PageKey, TabKey>;
  setActiveTab: (page: PageKey, tab: TabKey) => void;

  // Category Filters (per page) - persisted
  categoryFilters: Record<PageKey, CategoryFilter>;
  setCategoryFilter: (page: PageKey, categoryId: string | null) => void;
  setSubCategoryFilter: (page: PageKey, subCategoryId: string | null) => void;
  clearCategoryFilters: (page: PageKey) => void;

  // Page Filters (per page) - persisted
  pageFilters: Record<PageKey, PageFilters>;
  setOwnerFilter: (page: PageKey, ownerId: string | null) => void;
  setItemGroupFilter: (page: PageKey, itemGroup: string | null) => void;
  setStatusFilter: (page: PageKey, status: string | null) => void;
  setPriorityFilter: (page: PageKey, priority: string | null) => void;
  clearPageFilters: (page: PageKey) => void;
  resetAllOwnerFilters: (ownerId: string | null) => void;

  // View Mode (per page) - persisted
  viewMode: Record<PageKey, ViewMode>;
  setViewMode: (page: PageKey, mode: ViewMode) => void;

  // Grouping (per page) - persisted
  groupBy: Record<PageKey, GroupBy>;
  setGroupBy: (page: PageKey, groupBy: GroupBy) => void;

  // Calendar Navigation (per page, per view mode)
  calendarOffset: Record<PageKey, { weekly: number; monthly: number; yearly: number }>;
  navigateCalendar: (page: PageKey, viewMode: 'weekly' | 'monthly' | 'yearly', direction: -1 | 1) => void;
  resetCalendarOffset: (page: PageKey, viewMode: 'weekly' | 'monthly' | 'yearly') => void;

  // List View Date Offset (for Past Week/Today/Next Week daily carousel)
  listDateOffset: Record<PageKey, number>;
  navigateListDate: (page: PageKey, direction: -1 | 1) => void;
  resetListDateOffset: (page: PageKey) => void;

  // Expanded Item IDs (for tile visibility persistence)
  expandedItemIds: string[];
  toggleItemExpanded: (itemId: string) => void;
}

const defaultPageFilters: PageFilters = {
  ownerFilter: null,
  itemGroupFilter: null,
  statusFilter: null,
  priorityFilter: null,
};

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      // Sidebar
      sidebarOpen: false, // Default closed on mobile
      sidebarCollapsed: false,
      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),

      // Reference Date (for Today tab carousel - affects Past Week/Next Week)
      referenceDate: {
        tasks: 0,
        expenses: 0,
        notes: 0,
        dates: 0,
        search: 0,
        choreWheel: 0,
        dashboard: 0,
        notifications: 0,
      },
      setReferenceDate: (page, date) =>
        set((state) => ({
          referenceDate: { ...state.referenceDate, [page]: date.getTime() },
        })),
      resetReferenceDate: (page) =>
        set((state) => ({
          referenceDate: { ...state.referenceDate, [page]: 0 },
        })),
      getReferenceDate: (page) => {
        const timestamp = get().referenceDate[page];
        if (timestamp === 0) {
          const now = new Date();
          return new Date(now.getFullYear(), now.getMonth(), now.getDate());
        }
        return new Date(timestamp);
      },

      // Modals
      activeModal: null,
      modalData: null,
      openModal: (type, data = {}) => set({ activeModal: type, modalData: data }),
      closeModal: () => set({ activeModal: null, modalData: null }),

      // Confirm dialog
      confirmConfig: null,
      showConfirm: (config) => set({ confirmConfig: config }),
      hideConfirm: () => set({ confirmConfig: null }),

      // Toasts
      toasts: [],
      addToast: (toast) => {
        const id = Math.random().toString(36).substring(7);
        set((state) => ({
          toasts: [...state.toasts, { ...toast, id }],
        }));

        // Auto remove after duration
        const duration = toast.duration ?? 1500;
        if (duration > 0) {
          setTimeout(() => {
            set((state) => ({
              toasts: state.toasts.filter((t) => t.id !== id),
            }));
          }, duration);
        }
      },
      removeToast: (id) =>
        set((state) => ({
          toasts: state.toasts.filter((t) => t.id !== id),
        })),

      // Loading
      globalLoading: false,
      setGlobalLoading: (loading) => set({ globalLoading: loading }),

      // Search
      searchQuery: '',
      setSearchQuery: (query) => set({ searchQuery: query }),
      searchTab: 'scheduled' as SearchTabKey,
      setSearchTab: (tab) => set({ searchTab: tab }),

      // Tab Filters (per page) - default to 'today' for item dashboards
      activeTab: {
        tasks: 'today',
        expenses: 'today',
        notes: 'today',
        dates: 'today',
        search: 'allScheduled',  // Search will handle its own tabs separately
        choreWheel: 'today',
        dashboard: 'allScheduled',
        notifications: 'all',
      },
      setActiveTab: (page, tab) =>
        set((state) => ({
          activeTab: { ...state.activeTab, [page]: tab },
        })),

      // Category Filters (per page)
      categoryFilters: {
        tasks: { categoryId: null, subCategoryId: null },
        expenses: { categoryId: null, subCategoryId: null },
        notes: { categoryId: null, subCategoryId: null },
        dates: { categoryId: null, subCategoryId: null },
        search: { categoryId: null, subCategoryId: null },
        choreWheel: { categoryId: null, subCategoryId: null },
        dashboard: { categoryId: null, subCategoryId: null },
        notifications: { categoryId: null, subCategoryId: null },
      },
      setCategoryFilter: (page, categoryId) =>
        set((state) => ({
          categoryFilters: {
            ...state.categoryFilters,
            [page]: { categoryId, subCategoryId: null }, // Reset subcategory when category changes
          },
          // Also clear itemGroupFilter when category changes (cascade)
          pageFilters: {
            ...state.pageFilters,
            [page]: { ...state.pageFilters[page], itemGroupFilter: null },
          },
        })),
      setSubCategoryFilter: (page, subCategoryId) =>
        set((state) => ({
          categoryFilters: {
            ...state.categoryFilters,
            [page]: { ...state.categoryFilters[page], subCategoryId },
          },
          // Also clear itemGroupFilter when subCategory changes (cascade)
          pageFilters: {
            ...state.pageFilters,
            [page]: { ...state.pageFilters[page], itemGroupFilter: null },
          },
        })),
      clearCategoryFilters: (page) =>
        set((state) => ({
          categoryFilters: {
            ...state.categoryFilters,
            [page]: { categoryId: null, subCategoryId: null },
          },
        })),

      // Page Filters (per page)
      pageFilters: {
        tasks: { ...defaultPageFilters },
        expenses: { ...defaultPageFilters },
        notes: { ...defaultPageFilters },
        dates: { ...defaultPageFilters },
        search: { ...defaultPageFilters },
        choreWheel: { ...defaultPageFilters },
        dashboard: { ...defaultPageFilters },
        notifications: { ...defaultPageFilters },
      },
      setOwnerFilter: (page, ownerId) =>
        set((state) => ({
          pageFilters: {
            ...state.pageFilters,
            [page]: { ...state.pageFilters[page], ownerFilter: ownerId },
          },
        })),
      setItemGroupFilter: (page, itemGroup) =>
        set((state) => ({
          pageFilters: {
            ...state.pageFilters,
            [page]: { ...state.pageFilters[page], itemGroupFilter: itemGroup },
          },
        })),
      setStatusFilter: (page, status) =>
        set((state) => ({
          pageFilters: {
            ...state.pageFilters,
            [page]: { ...state.pageFilters[page], statusFilter: status },
          },
        })),
      setPriorityFilter: (page, priority) =>
        set((state) => ({
          pageFilters: {
            ...state.pageFilters,
            [page]: { ...state.pageFilters[page], priorityFilter: priority },
          },
        })),
      clearPageFilters: (page) =>
        set((state) => ({
          pageFilters: {
            ...state.pageFilters,
            [page]: { ...defaultPageFilters },
          },
        })),
      resetAllOwnerFilters: (ownerId) =>
        set((state) => ({
          pageFilters: {
            tasks: { ...state.pageFilters.tasks, ownerFilter: ownerId },
            expenses: { ...state.pageFilters.expenses, ownerFilter: ownerId },
            notes: { ...state.pageFilters.notes, ownerFilter: ownerId },
            dates: { ...state.pageFilters.dates, ownerFilter: ownerId },
            search: { ...state.pageFilters.search, ownerFilter: ownerId },
            choreWheel: { ...state.pageFilters.choreWheel, ownerFilter: ownerId },
            // Dashboard uses '' for "All Owners" (not null) to avoid triggering auto-default useEffect
            dashboard: { ...state.pageFilters.dashboard, ownerFilter: ownerId === null ? '' : ownerId },
            notifications: { ...state.pageFilters.notifications, ownerFilter: ownerId },
          },
        })),

      // View Mode (per page)
      viewMode: {
        tasks: 'card',
        expenses: 'card',
        notes: 'card',
        dates: 'card',
        search: 'card',
        choreWheel: 'card',
        dashboard: 'card',
        notifications: 'card',
      },
      setViewMode: (page, mode) =>
        set((state) => ({
          viewMode: { ...state.viewMode, [page]: mode },
        })),

      // Grouping (per page)
      groupBy: {
        tasks: 'date',
        expenses: 'date',
        notes: 'date',
        dates: 'date',
        search: 'date',
        choreWheel: 'priority',
        dashboard: 'date',
        notifications: 'date',
      },
      setGroupBy: (page, groupBy) =>
        set((state) => ({
          groupBy: { ...state.groupBy, [page]: groupBy },
        })),

      // Calendar Navigation (per page, per view mode)
      calendarOffset: {
        tasks: { weekly: 0, monthly: 0, yearly: 0 },
        expenses: { weekly: 0, monthly: 0, yearly: 0 },
        notes: { weekly: 0, monthly: 0, yearly: 0 },
        dates: { weekly: 0, monthly: 0, yearly: 0 },
        search: { weekly: 0, monthly: 0, yearly: 0 },
        choreWheel: { weekly: 0, monthly: 0, yearly: 0 },
        dashboard: { weekly: 0, monthly: 0, yearly: 0 },
        notifications: { weekly: 0, monthly: 0, yearly: 0 },
      },
      navigateCalendar: (page, viewMode, direction) =>
        set((state) => ({
          calendarOffset: {
            ...state.calendarOffset,
            [page]: {
              ...state.calendarOffset[page],
              [viewMode]: state.calendarOffset[page][viewMode] + direction,
            },
          },
        })),
      resetCalendarOffset: (page, viewMode) =>
        set((state) => ({
          calendarOffset: {
            ...state.calendarOffset,
            [page]: {
              ...state.calendarOffset[page],
              [viewMode]: 0,
            },
          },
        })),

      // List View Date Offset (for Past Week/Today/Next Week daily carousel)
      listDateOffset: {
        tasks: 0,
        expenses: 0,
        notes: 0,
        dates: 0,
        search: 0,
        choreWheel: 0,
        dashboard: 0,
        notifications: 0,
      },
      navigateListDate: (page, direction) =>
        set((state) => ({
          listDateOffset: {
            ...state.listDateOffset,
            [page]: state.listDateOffset[page] + direction,
          },
        })),
      resetListDateOffset: (page) =>
        set((state) => ({
          listDateOffset: { ...state.listDateOffset, [page]: 0 },
        })),

      // Expanded Item IDs
      expandedItemIds: [],
      toggleItemExpanded: (itemId) =>
        set((state) => ({
          expandedItemIds: state.expandedItemIds.includes(itemId)
            ? state.expandedItemIds.filter((id) => id !== itemId)
            : [...state.expandedItemIds, itemId],
        })),
    }),
    {
      name: 'perfin-ui-store',
      partialize: (state) => ({
        // Only persist these fields
        activeTab: state.activeTab,
        categoryFilters: state.categoryFilters,
        pageFilters: state.pageFilters,
        viewMode: state.viewMode,
        groupBy: state.groupBy,
        listDateOffset: state.listDateOffset,
        calendarOffset: state.calendarOffset,
        referenceDate: state.referenceDate,
        sidebarCollapsed: state.sidebarCollapsed,
        expandedItemIds: state.expandedItemIds,
      }),
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<UIState> | undefined;
        return {
          ...currentState,
          ...persisted,
          // Deep merge activeTab to ensure all pages have a default value
          activeTab: {
            tasks: 'today' as TabKey,
            expenses: 'today' as TabKey,
            notes: 'today' as TabKey,
            dates: 'today' as TabKey,
            search: 'allScheduled' as TabKey,
            choreWheel: 'today' as TabKey,
            dashboard: 'allScheduled' as TabKey,
            notifications: 'all' as TabKey,
            ...persisted?.activeTab,
          },
        };
      },
    }
  )
);
